import { useEffect, useRef, useState } from "react";
import { ActionModal, Button, Field, Switch } from "@cntrl/ui";
import { ApiError } from "../../app/hooks/apiError.js";
import {
  useDisableMfa,
  useEnableMfa,
  useForgetTrustedDevices,
  useMfaStatus,
  useStartEnableMfa,
} from "../../app/hooks/useMfa.js";
import styles from "./SettingsScreen.module.scss";

/**
 * "Enable MFA" (ART-317) — on both Settings › Account tabs. Person-scoped, so one component serves the band and
 * the roster/org shells.
 *
 * Turning it ON emails a code and needs it back, which proves the address receives mail before sign-in starts
 * depending on it. Turning it OFF needs the current password, so a borrowed signed-in device cannot switch it off.
 * With it on, a password sign-in asks for an emailed code unless the device was trusted on an earlier code; a
 * passkey sign-in never asks. "Forget trusted devices" makes every trusted device ask again.
 */
export function MfaSection() {
  const status = useMfaStatus();
  const forget = useForgetTrustedDevices();
  const [dialog, setDialog] = useState<"enable" | "disable" | null>(null);

  const enabled = status.data?.enabled ?? false;
  const trusted = status.data?.trustedDevices ?? 0;

  return (
    <>
      <div className={styles.toggleRow}>
        <span className={styles.toggleText}>
          <span className={styles.toggleLabel}>Enable MFA</span>
          <span className={styles.toggleDesc}>Get a sign in code to your email when signing in.</span>
        </span>
        <Switch
          checked={enabled}
          disabled={status.isLoading || status.isError}
          onChange={(next) => setDialog(next ? "enable" : "disable")}
          label="Enable MFA"
        />
      </div>
      {enabled && trusted > 0 && (
        <div className={styles.accountLinks}>
          <Button type="button" variant="link" loading={forget.isPending} onClick={() => forget.mutate()}>
            {trusted === 1 ? "Forget trusted device" : `Forget ${trusted} trusted devices`}
          </Button>
        </div>
      )}
      {dialog === "enable" && <EnableMfaDialog onClose={() => setDialog(null)} />}
      {dialog === "disable" && <DisableMfaDialog onClose={() => setDialog(null)} />}
    </>
  );
}

function EnableMfaDialog({ onClose }: { onClose: () => void }) {
  const start = useStartEnableMfa();
  const enable = useEnableMfa();
  const [code, setCode] = useState("");
  const startMutate = start.mutate;
  const sent = useRef(false);

  // The code goes out as the dialog opens: the dialog IS the confirmation step. Once — a second send (StrictMode's
  // double effect) would mail a second code and leave the first one, the one in the inbox, unusable.
  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    startMutate();
  }, [startMutate]);

  const challenge = start.data;
  const error = start.isError
    ? start.error instanceof ApiError && start.error.status === 429
      ? "Too many codes requested. Wait a few minutes and try again."
      : "Couldn't send the code. Please try again."
    : enable.isError
      ? enable.error instanceof ApiError && enable.error.status === 401
        ? "That code didn't work — check it or close and try again."
        : "Couldn't turn on MFA. Please try again."
      : null;

  return (
    <ActionModal
      title="Enable MFA"
      confirmLabel="Turn on"
      confirmDisabled={!challenge || code.length !== 6}
      loading={enable.isPending}
      onCancel={onClose}
      onConfirm={() =>
        challenge?.challengeToken &&
        enable.mutate({ challengeToken: challenge.challengeToken, code }, { onSuccess: onClose })
      }
    >
      <p>{challenge ? `We sent a 6-digit code to ${challenge.email}.` : "Sending a code to your email…"}</p>
      <Field
        label="Code"
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="123456"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
      />
      {error && <p role="alert">{error}</p>}
    </ActionModal>
  );
}

function DisableMfaDialog({ onClose }: { onClose: () => void }) {
  const disable = useDisableMfa();
  const [password, setPassword] = useState("");
  const error = disable.isError
    ? disable.error instanceof ApiError && disable.error.status === 401
      ? "That password isn't right."
      : disable.error instanceof ApiError && disable.error.status === 429
        ? "Too many attempts. Wait a few minutes and try again."
        : "Couldn't turn off MFA. Please try again."
    : null;

  return (
    <ActionModal
      title="Turn off MFA"
      confirmLabel="Turn off"
      confirmTone="danger"
      confirmDisabled={!password}
      loading={disable.isPending}
      onCancel={onClose}
      onConfirm={() => disable.mutate(password, { onSuccess: onClose })}
    >
      <p>Enter your password to turn off sign-in codes. Your trusted devices will be forgotten.</p>
      <Field
        label="Current password"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      {error && <p role="alert">{error}</p>}
    </ActionModal>
  );
}
